import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet'
import { message, Typography, Button, Row, Col, Space } from 'antd'
import './style.less'
import { createUseStyles } from 'react-jss'
import { CloseOutlined, SearchOutlined, ShopOutlined, EnvironmentOutlined } from '@ant-design/icons'
import { mapValues } from 'lodash'
import { getData, setOnlyDelivery, setFilter } from '@/store/slice/data'
import Filter from '@components/Filter'
import StoreList from '@components/StoreList'
import SectionTitle from '@components/SectionTitle'
import GoogleMap from '@components/GoogleMap'
import area from '@data/area.js'
import { initVal } from '@data/filter.js'
import * as grid from '@data/grid.js'

const { Title } = Typography

const useStyles = createUseStyles({
  onlyDeliveryfilter: {
    display: props => props.onlyDelivery ? 'none' : 'block'
  }
}, { name: 'StoresMap' })

const StoresMap = (props) => {
  const { title, subtitle, onlyDelivery } = props
  const dispatch = useDispatch()
  const style = useStyles(props)
  const products = useSelector((state) => state.data.productList)
  const stores = useSelector((state) => state.data.storeList)
  const filter = useSelector((state) => state.data.filter)
  // record pre searched store items
  const [storeBucket, setStoreBucket] = useState([])
  const [isDataLoaded, setDataLoaded] = useState(false)
  const gmapRef = useRef(null)

  useEffect(() => {
    dispatch(setOnlyDelivery(onlyDelivery))
    async function loadingData(params) {
      try {
        message.loading('門市資料載入中')
        await dispatch(getData()).unwrap()
        onResetFilter()
        setDataLoaded(true)
      } catch (error) {
        message.error('門市據點資料載入錯誤，請確認您的網路狀態，如果網路沒問題卻發生錯誤，請和客服聯絡')
      }
    }
    loadingData()
    // We just run once at initial pharse, so don't need any variable dependency
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (stores.length > 0 && isDataLoaded === true) {
      setStoreBucket(stores)
      // 設定成為null，讓isDataLoaded永遠不會等於true，就永遠不會再進入這個流程
      setDataLoaded(null)
    }
  }, [isDataLoaded, stores])

  /** Filter Component Functions Start*/
  const onApplyFilter = useCallback(() => {
    setStoreBucket(stores)
  }, [stores])

  const onResetFilter = useCallback(() => {
    const initFilter = mapValues(filter, () => initVal)
    dispatch(setFilter(initFilter))
  }, [dispatch, filter])
  /** Filter Component Functions End*/

  const onStoreClick = useCallback((store) => {
    if (gmapRef) gmapRef.current.focus(store)
  }, [])

  /** Filter Component Calculation Start*/
  const districtList = useMemo(() => {
    return filter.city === initVal ? [] : area.districts[filter.city]
  }, [filter])

  const { cities, districts, channels } = useMemo(() => {
    const { cities, districts, channels } = stores.reduce((obj, store) => {
      obj.cities.add(store.city)
      if (districtList.includes(store.district)) {
        obj.districts.add(store.district)
      }
      obj.channels.add(store.channel)
      return obj
    }, { cities: new Set(), districts: new Set(), channels: new Set() })

    return {
      cities: Array.from(cities),
      districts: Array.from(districts),
      channels: Array.from(channels)
    }
  }, [stores, districtList])

  const cityList = useMemo(() => {
    return cities.sort((cityA, cityB) => {
      console.log(cityA, area.cities.indexOf(cityA))
      console.log(cityB, area.cities.indexOf(cityB))
      return area.cities.indexOf(cityA) - area.cities.indexOf(cityB)
    })
  }, [cities])
  console.log(cityList)
  const { brands, goods, periods } = useMemo(() => {
    const { brands, goods, periods } = products.reduce((obj, product) => {
      obj.brands.add(product.brand)
      obj.goods.push(product.name)
      obj.periods.add(product.period)
      return obj
    }, { brands: new Set(), goods: [], periods: new Set() })

    return {
      brands: Array.from(brands),
      goods,
      periods: Array.from(periods)
    }
  }, [products])
  /** Filter Component Calculation End*/

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <section className="text-center">
        <div className="StoresMap-title-wrapper">
          <Title level={1} className="StoresMap-title">{title}</Title>
          <Title level={5} className="StoresMap-subtitle">{subtitle}</Title>
        </div>
      </section>

      <section>
        <SectionTitle icon={<EnvironmentOutlined />} title="依地區搜尋" />
        <Row gutter={grid.gutter}>
          <Col {...grid.filterCol.region}>
            <Filter name="縣市" type="city" choices={cityList} />
          </Col>
          <Col {...grid.filterCol.region}>
            <Filter name="地區" type="district" choices={districts} />
          </Col>
        </Row>
      </section>

      <section>
        <SectionTitle icon={<ShopOutlined />} title="依通路/產品搜尋" />
        <Row gutter={grid.gutter}>
          <Col {...grid.filterCol.product}>
            <Filter name="通路" type="channel" choices={channels} />
          </Col>
          <Col {...grid.filterCol.product} className={style.onlyDeliveryfilter}>
            <Filter name="品牌" type="brand" choices={brands} />
          </Col>
          <Col {...grid.filterCol.product} className={style.onlyDeliveryfilter}>
            <Filter name="品名" type="product" choices={goods} />
          </Col>
          <Col {...grid.filterCol.product} className={style.onlyDeliveryfilter}>
            <Filter name="週期" type="period" choices={periods} />
          </Col>
        </Row>
      </section>

      <section className="mt-3">
        <Row gutter={grid.gutter} align="middle">
          <Col {...grid.infoCol.left} className="StoresMap-info text-center">- 重新查詢請按「清空搜尋」按鈕 -</Col>
          <Col {...grid.infoCol.right} className="text-right">
            <Space>
              <Button type="primary" icon={<CloseOutlined />} onClick={onResetFilter}>清空搜尋</Button>
              <Button type="primary" icon={<SearchOutlined />} onClick={onApplyFilter}>開始搜尋</Button>
            </Space>
          </Col>
        </Row>
      </section>

      <Row gutter={grid.gutter} className="mt-3">
        <Col {...grid.noticeCol}>
          <section className="StoresMap-notice w-100 flex-hv-center">
            ＊各產品實際度數與庫存，請依現場門市為主。
          </section>
        </Col>
        <Col {...grid.mapCol}>
          <section className="StoresMap-window">
            <GoogleMap ref={gmapRef} stores={storeBucket} onMarkerClick={onStoreClick} />
          </section>
        </Col>
        <Col {...grid.listCol}>
          <section className="StoresMap-window">
            <div className="StoresMap-search-result mb-3">{storeBucket.length}個搜尋結果</div>
            <div id="storeList" className="StoresMap-store-list">
              <StoreList stores={storeBucket} wrapperId="storeList"></StoreList>
            </div>
          </section>
        </Col>
      </Row>
    </div>
  )
}

StoresMap.defaultProps = {
  title: '實體購物門市據點',
  onlyDelivery: false,
}

export default StoresMap